import React, { FC, useState, useCallback, useEffect, ReactNode, useMemo, memo } from 'react';

import { Checkbox, FormControl, Button, SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';

import { StyledMenuItem, StyledSelect } from '@/components/Select/styles';
import { areArraysEqual } from '@/utils';

import { MultiSelectProps } from './types';

import styles from './styles.module.scss';

import ArrowDown from '@/assets/icons/arrow-down.svg?react';

const ApplyButton = styled(Button)({
  width: '100%',
  color: '#fff',
  backgroundColor: '#2b39b9',
  borderRadius: '0 0 4px 4px',
  fontSize: 12,
  height: 48,
  letterSpacing: 0.35,
  '&:hover': {
    backgroundColor: '#1b2575',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E5E9F2',
    color: '#8F95B2',
  },
});

const MultiSelectComponent: FC<MultiSelectProps> = ({
  defaultOptions,
  selectedOptions,
  onChange,
  label,
  name,
  ...props
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [tempSelectedItems, setTempSelectedItems] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  const disableApplyChanges = useMemo(
    () => areArraysEqual(tempSelectedItems, selectedOptions) || tempSelectedItems.length === 0,
    [tempSelectedItems, selectedOptions],
  );

  const handleChange = useCallback((event: SelectChangeEvent<unknown>, _: ReactNode) => {
    const value = event.target.value as string[];
    setTempSelectedItems(value);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTempSelectedItems(selectedItems);
  }, [selectedItems]);

  const handleOpen = useCallback(() => setOpen(true), []);

  const handleApply = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSelectedItems(tempSelectedItems);
      setOpen(false);

      if (onChange) {
        onChange(tempSelectedItems);
      }
    },
    [tempSelectedItems, onChange],
  );

  const renderValue = useCallback((selected: unknown) => {
    const selectedValues = selected as string[];
    if (selectedValues.length === 0) {
      return <span className={styles.listItem}>Select options</span>;
    }

    return (
      <div className={styles.selectedValues}>
        <span className={styles.selectedValuesTitle}>{selectedValues.join(', ')}</span>
      </div>
    );
  }, []);

  useEffect(() => {
    setTempSelectedItems(selectedItems);
  }, [selectedItems, open]);

  useEffect(() => {
    if (selectedOptions?.length) {
      setSelectedItems(selectedOptions);
      setTempSelectedItems(selectedOptions);
    }
  }, [selectedOptions]);

  return (
    <FormControl variant="outlined">
      <StyledSelect
        multiple
        id={name}
        name={name}
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        value={tempSelectedItems}
        onChange={handleChange}
        renderValue={renderValue}
        IconComponent={() => <ArrowDown className={styles.selectArrow} color="#2b39b9" />}
        MenuProps={{
          sx: {
            '& .MuiList-root': {
              padding: 0,
            },
          },
          PaperProps: {
            style: {
              maxHeight: 360,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          slotProps: {
            paper: {
              sx: {
                marginTop: '5px',
                width: 'auto',
                minWidth: '100%',
              },
            },
          },
          disableScrollLock: true,
          autoFocus: false,
        }}
        {...props}
      >
        {defaultOptions.map((option) => (
          <StyledMenuItem key={option} value={option}>
            <Checkbox checked={tempSelectedItems.indexOf(option) > -1} />
            <span className={styles.listItem}>{option}</span>
          </StyledMenuItem>
        ))}

        <div className={styles.applyButtonWrapper}>
          <ApplyButton variant="contained" onClick={handleApply} disabled={disableApplyChanges}>
            Apply Changes
          </ApplyButton>
        </div>
      </StyledSelect>
    </FormControl>
  );
};

export const MultiSelect = memo(MultiSelectComponent);
